import {
  ClubOnboardingSettingsDTO,
  Iso2Country,
  UserMemberProfileDTOPatch,
} from "@gymflow/types";
import { DateTime } from "luxon";
import { z } from "zod";

import { validatePhoneNumber } from "./lib/helpers/phoneNumber";
import { validatePostCode } from "./lib/helpers/postCode";

const mapSettingsToFields = {
  mobileNumber: "mobileNumberSetting",
  dateBirth: "dateBirthSetting",
  gender: "genderSetting",
  personalNumber: "personalNumberSetting",
  addressLine1: "addressSetting",
  addressLine2: "addressSetting",
  postCode: "postCodeSetting",
  city: "citySetting",
  emergencyContact: "emergencyContactSetting",
  emergencyContactName: "emergencyContactSetting",
  picture: "pictureSetting",
} satisfies Record<
  keyof UserMemberProfileDTOPatch | "picture",
  keyof ClubOnboardingSettingsDTO
>;

const schemaEntries = (country: Iso2Country, mobileNumber: string) =>
  ({
    mobileNumber: z.string().refine((e) => validatePhoneNumber(e), {
      message: "validation.phoneNumber",
    }),
    dateBirth: z.string().refine(
      (e) => {
        return e === undefined || DateTime.fromISO(e) < DateTime.now();
      },
      {
        message: "validation.dateTooBig",
        params: {
          maximum: DateTime.now().toLocaleString(DateTime.DATE_MED),
        },
      },
    ),
    gender: z.enum(["MALE", "FEMALE", "PREFER_NOT_TO_SAY"]),
    personalNumber: z.string().min(1, "common.required"),
    addressLine1: z.string().min(3),
    addressLine2: z.string().optional(),
    postCode: z
      .string()
      .trim()
      .refine((e) => validatePostCode(e, country), {
        message: "validation.postCode",
      }),
    city: z
      .string()
      .refine((val) => !val || val.trim() === "" || val.length >= 3, {
        message: "validation.minCharacters",
        params: {
          minimum: 3,
        },
      }),
    emergencyContactName: z.string().min(3),
    emergencyContact: z
      .string()
      .refine((e) => validatePhoneNumber(e), {
        message: "validation.phoneNumber",
      })
      .refine((e) => e !== mobileNumber, {
        message: "validation.noDuplicateContact",
      }),
    picture: z.string(),
  }) satisfies Record<
    keyof UserMemberProfileDTOPatch | "picture",
    z.ZodTypeAny
  >;

const steps = [
  "personalDetails",
  "addressDetails",
  "emergencyContactDetails",
] as const;

const formSchemaKeys = {
  personalDetails: [
    "mobileNumber",
    "dateBirth",
    "gender",
    "personalNumber",
    "picture",
  ],
  addressDetails: ["addressLine1", "addressLine2", "postCode", "city"],
  emergencyContactDetails: ["emergencyContactName", "emergencyContact"],
} satisfies {
  [key in (typeof steps)[number]]: (
    | keyof UserMemberProfileDTOPatch
    | "picture"
  )[];
};

export const OnboardingSchemaHelper = {
  generator: (
    settings: ClubOnboardingSettingsDTO,
    country: Iso2Country,
    mobileNumber: string,
  ) => {
    const perCountrySchemaEntries = schemaEntries(country, mobileNumber);
    return Object.entries(formSchemaKeys)
      .map((params) => {
        const [key, value] = params as [
          string,
          (keyof UserMemberProfileDTOPatch)[],
        ];
        let innerSchema = z.object({});
        value.forEach((key) => {
          const setting = settings[mapSettingsToFields[key]];
          if (setting !== "DISABLED") {
            innerSchema = innerSchema.extend({
              [key]:
                setting === "OPTIONAL"
                  ? perCountrySchemaEntries[key].optional()
                  : perCountrySchemaEntries[key],
            });
          }
        });
        return [key, innerSchema] as const;
      })
      .reduce(
        (acc, curr) => ({ ...acc, [curr[0]]: curr[1] }),
        {} as Record<keyof typeof formSchemaKeys, z.ZodTypeAny>,
      );
  },
  getSteps: (settings: ClubOnboardingSettingsDTO) => {
    return steps.filter(
      (step) =>
        !formSchemaKeys[step].every(
          (k) => settings[mapSettingsToFields[k]] === "DISABLED",
        ),
    );
  },
  formSchemaKeys,
  defaultValues: {
    mobileNumber: undefined,
    dateBirth: undefined,
    gender: undefined,
    personalNumber: undefined,
    addressLine1: undefined,
    addressLine2: undefined,
    postCode: undefined,
    city: undefined,
    emergencyContactName: undefined,
    emergencyContact: undefined,
    picture: undefined,
  },
};
